import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Upload, Button, Typography, App } from 'antd';
import { InboxOutlined, DeleteOutlined, FileImageOutlined } from '@ant-design/icons';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../../config/firebase';
import { primaryShades } from '../../../theme/theme';

const { Paragraph } = Typography;
const { Dragger } = Upload;

/**
 * Component for collecting additional information in the bulk order process
 * 
 * @param {Object} props - Component properties
 * @param {Function} props.onDataChange - Callback when additional info data changes
 * @param {Object} props.initialData - Initial data if editing
 * @param {string} props.assetOrderId - Asset order ID for image storage path
 */
const AdditionalInfoStep = forwardRef(({ onDataChange, initialData = {}, assetOrderId }, ref) => {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [uploading, setUploading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imageUrl, setImageUrl] = useState(initialData.image || null);

    // Effect to set initial form values
    useEffect(() => {
        form.setFieldsValue({
            batch: initialData.batch || ''
        });
    }, [initialData, form]);

    // Function to update parent data
    const updateParentData = useCallback(() => {
        const values = form.getFieldsValue();
        const batchValue = values.batch || '';
        const additionalInfo = {
            batch: batchValue,
            name: batchValue, // Usa il valore del batch anche come name
            image: imageUrl || ''
        };
        onDataChange(additionalInfo);
    }, [form, imageUrl, onDataChange]);

    // Handle key press for updating data on Enter
    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            updateParentData();
        }
    }, [updateParentData]);

    // Expose updateData function to parent via ref
    useImperativeHandle(ref, () => ({
        updateData: updateParentData
    }));

    // Function to generate unique filename
    const generateFileName = (originalName) => {
        const timestamp = Date.now();
        const extension = originalName.split('.').pop();
        return `image_${timestamp}.${extension}`;
    };

    // Function to format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Function to handle image upload
    const handleImageUpload = async (file) => {
        if (!assetOrderId) {
            message.error('Asset order ID not available. Please try again.');
            return false;
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            message.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
            return false;
        }

        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            message.error('File size must be less than 10MB');
            return false;
        }

        setUploading(true);

        try {
            // Create storage reference
            const fileName = generateFileName(file.name);
            const imageStorageRef = storageRef(storage, `CatalogOrders/${assetOrderId}/${fileName}`);

            // Delete previous image if exists
            if (imageUrl) {
                try {
                    const previousRef = storageRef(storage, imageUrl);
                    await deleteObject(previousRef);
                } catch (deleteError) {
                    console.warn('Could not delete previous image:', deleteError);
                    // Continue with upload even if delete fails
                }
            }

            // Upload file
            const snapshot = await uploadBytes(imageStorageRef, file);

            // Get download URL
            const downloadURL = await getDownloadURL(snapshot.ref);

            setImageUrl(downloadURL);
            setImageFile(file);

            message.success('Image uploaded successfully');

            // Update parent component with new image URL
            const values = form.getFieldsValue();
            const batchValue = values.batch || '';
            const additionalInfo = {
                batch: batchValue,
                name: batchValue, // Usa il valore del batch anche come name
                image: downloadURL
            };
            onDataChange(additionalInfo);

        } catch (error) {
            console.error('Error uploading image:', error);
            message.error('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }

        return false; // Prevent default upload behavior
    };

    // Function to handle image removal
    const handleImageRemove = async () => {
        if (imageUrl) {
            try {
                const imageRef = storageRef(storage, imageUrl);
                await deleteObject(imageRef);
                message.success('Image removed successfully');
            } catch (error) {
                console.warn('Could not delete image from storage:', error);
                // Continue with removal even if delete fails
            }
        }

        setImageUrl(null);
        setImageFile(null);

        // Update parent component with no image
        const values = form.getFieldsValue();
        const batchValue = values.batch || '';
        const additionalInfo = {
            batch: batchValue,
            name: batchValue, // Usa il valore del batch anche come name
            image: ''
        };
        onDataChange(additionalInfo);
    };

    return (
        <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem 0',
            gap: '1rem'
        }}>
            {/* Header section */}
            <div style={{ flexShrink: 0 }}>
                <Paragraph style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                    Add information to your order for better organization and tracking.
                </Paragraph>
            </div>

            {/* Form fields */}
            <div style={{ flexShrink: 0 }}>
                <Form
                    form={form}
                    layout="vertical"
                    style={{ maxWidth: '600px' }}
                >
                    <Form.Item
                        label="Batch"
                        name="batch"
                        tooltip="Enter a batch identifier to group related orders"
                        style={{ marginBottom: '1rem' }}
                        rules={[
                            {
                                required: true,
                                message: 'Please enter a batch identifier'
                            },
                            {
                                min: 1,
                                message: 'Batch identifier cannot be empty'
                            }
                        ]}
                    >
                        <Input
                            placeholder="Enter batch identifier"
                            maxLength={100}
                            onKeyDown={handleKeyPress}
                            onBlur={updateParentData}
                        />
                    </Form.Item>
                </Form>
            </div>

            {/* Image upload section */}
            <div style={{ flexShrink: 0 }}>
                <div style={{ marginBottom: '0.5rem' }}>
                    <Paragraph strong style={{ margin: 0, fontSize: '0.875rem' }}>
                        Batch Logo Image (Optional)
                    </Paragraph>
                    <Paragraph type="secondary" style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem' }}>
                        Upload an optional logo image for the batch (max 10MB)
                    </Paragraph>
                </div>

                {!imageUrl ? (
                    <Dragger
                        accept="image/*"
                        beforeUpload={handleImageUpload}
                        fileList={[]}
                        showUploadList={false}
                        style={{
                            padding: '0.75rem',
                            minHeight: '5rem',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                        disabled={uploading}
                    >
                        <p className="ant-upload-drag-icon" style={{ fontSize: '1.5rem', margin: '0 0 0.25rem 0' }}>
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text" style={{ margin: '0', fontSize: '0.875rem' }}>
                            {uploading ? 'Uploading...' : 'Click or drag an image here'}
                        </p>
                        <p className="ant-upload-hint" style={{ margin: '0', fontSize: '0.75rem' }}>
                            JPEG, PNG, GIF, WebP (max 10MB)
                        </p>
                    </Dragger>
                ) : (
                    <div style={{
                        padding: '0.75rem',
                        backgroundColor: `${primaryShades.primary100}`,
                        border: `1px solid ${primaryShades.primary300}`,
                        borderRadius: '0.375rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                    }}>
                        {/* Image preview thumbnail */}
                        <img
                            src={imageUrl}
                            alt="Uploaded reference"
                            style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #f0f0f0',
                                flexShrink: 0
                            }}
                        />

                        {/* File info */}
                        <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: primaryShades.primary700,
                                marginBottom: '0.125rem'
                            }}>
                                {imageFile?.name || 'Reference image'}
                            </div>
                            <div style={{
                                fontSize: '0.75rem',
                                color: primaryShades.primary600
                            }}>
                                {imageFile?.size ? formatFileSize(imageFile.size) : 'Uploaded'}
                            </div>
                        </div>

                        {/* Remove button */}
                        <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={handleImageRemove}
                            style={{
                                color: primaryShades.primary500,
                                flexShrink: 0
                            }}
                            title="Remove image"
                        />
                    </div>
                )}
            </div>
        </div>
    );
});

export default AdditionalInfoStep;
